// Collateral management types — mirror the Java DTOs under
// webapps/scalefi-essence/src/main/java/tz/scalefi/webapp/essence/collateral/dto
//
// Collaterals are standalone (branch-scoped) entities. Each collateral has a
// surrogate numeric id and a human-readable `code`. A collateral can be linked
// to zero or more loans via `LoanCollateralLinkDTO`.

export interface Collateral {
    id?: number;
    branch?: string;              // assigned from server context on create
    code: string;                 // human-readable, unique within a branch
    collateralType: string;
    description?: string;
    estimatedValue?: number;
    valuationDate?: string;       // ISO date (YYYY-MM-DD)
    ownerName?: string;
    ownerCustNo?: string;
    registrationNo?: string;
    registrationDate?: string;
    location?: string;
    insurancePolicyNo?: string;
    insuranceExpiry?: string;
    custodyLocation?: string;
    status?: string;              // AVAILABLE / PLEDGED / UNDER_REVIEW / RELEASED
    lienMark?: boolean;
    lienExpiry?: string;
    remarks?: string;
    createdAt?: string;
    createdBy?: string;
    updatedAt?: string;
    updatedBy?: string;
}

export interface CollateralDocument {
    id?: number;
    collateralId?: number;
    documentType: string;
    documentName?: string;
    fileId: string;
    fileName?: string;
    fileUrl?: string;
    fileSize?: number;
    mimeType?: string;
    uploadedAt?: string;
    uploadedBy?: string;
    notes?: string;
}

/**
 * A single (collateral ↔ loan) link row.
 *
 * The loan side is a composite key (branch, glNo, acNo, cycle). The link table
 * enforces uniqueness on (collateralId, branch, glNo, acNo, cycle) so the same
 * asset cannot be pledged twice against the same loan cycle.
 */
export interface LoanCollateralLink {
    id?: number;
    collateralId: number;
    branch: string;
    glNo: string;
    acNo: string;
    cycle: number;
    pledgedValue?: number;
    status?: string;              // ACTIVE / RELEASED
    notes?: string;
    linkedAt?: string;
    linkedBy?: string;
    releasedAt?: string;
    releasedBy?: string;
    // Projected from the related collateral for list UIs
    collateralCode?: string;
    collateralType?: string;
    collateralEstimatedValue?: number;
}

/** Wrapper returned by GET /api/collaterals/{id}. */
export interface CollateralDetailResponse {
    collateral: Collateral;
    documents: CollateralDocument[];
    links: LoanCollateralLink[];
}

/** Spring Data Page<T> shape as serialised by the backend. */
export interface Page<T> {
    content: T[];
    totalElements: number;
    totalPages: number;
    number: number;
    size: number;
    first?: boolean;
    last?: boolean;
    empty?: boolean;
}

/** Search parameters for GET /api/collaterals. */
export interface CollateralSearchParams {
    branch?: string;
    q?: string;
    status?: string;
    page?: number;
    size?: number;
}

/** Body for POST /api/loans/{...}/collateral-links. */
export interface LinkCollateralRequest {
    collateralId: number;
    pledgedValue?: number;
    notes?: string;
}

/** Compound key identifying a loan — matches the backend URL segments. */
export interface LoanKey {
    branch: string;
    glNo: string;
    acNo: string;
    cycle: number;
}

/** Standard collateral types — loosely matched to BOT regulation guidance. */
export const COLLATERAL_TYPES = [
    'VEHICLE',
    'LAND',
    'BUILDING',
    'MACHINERY',
    'STOCK',
    'CASH_DEPOSIT',
    'SHARES',
    'OTHER',
] as const;

export const COLLATERAL_STATUSES = [
    'AVAILABLE',
    'PLEDGED',
    'UNDER_REVIEW',
    'RELEASED',
] as const;

export const DOCUMENT_TYPES = [
    'TITLE_DEED',
    'VALUATION_REPORT',
    'PHOTO',
    'INSURANCE_CERT',
    'REGISTRATION',
    'OTHER',
] as const;
